/**
 * BulkRemoveApprovalChainDialog — take one approval chain off many selected assets at once
 *
 *
 * The unassign endpoint is scoped to a single chain, so the dialog works from the
 * chains actually present in the selection rather than the whole project list: one
 * chain is preselected with no picker, several offer a picker limited to those.
 * Assets on a different chain are reported through the `skipped` count.
 *
 * Removing a chain sends any asset it had fully approved back to To review, which the
 * confirmation says out loud — it is the part a user cannot undo by re-assigning.
 *
 * Error UX is owned here.
 */
import { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@monksflow/monks-ui';

import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';

import { useBulkUnassignApprovalChain } from '../../api/useBulkUnassignApprovalChain.mutation';
import { useTranslation } from 'react-i18next';

/** One approval chain found on at least one selected asset. */
export interface SelectedAssetApprovalChain {
  id: string;
  name: string;
  assetIds: string[];
}

export interface BulkRemoveApprovalChainDialogProps {
  open: boolean;
  projectId: string;
  /** Folder in view — threaded to the mutation so the files list refreshes. */
  folderId?: string;
  /** Chains present across the selected assets, with the assets on each. */
  chainsInSelection: SelectedAssetApprovalChain[];
  /** Total assets the user selected, including any with no approval chain. */
  selectedCount: number;
  onClose: () => void;
  /** Called after a successful removal so the host can clear its selection. */
  onRemoved?: () => void;
}

export function BulkRemoveApprovalChainDialog({
  open,
  projectId,
  folderId,
  chainsInSelection,
  selectedCount,
  onClose,
  onRemoved,
}: BulkRemoveApprovalChainDialogProps) {
  const { t } = useTranslation('projects');

  const bulkUnassign = useBulkUnassignApprovalChain({ projectId, folderId });

  const onlyChainId = chainsInSelection.length === 1 ? chainsInSelection[0].id : null;
  const [pickedChainId, setPickedChainId] = useState<string | null>(null);
  const approvalChainId = onlyChainId ?? pickedChainId;

  const selectedChain = useMemo(
    () => chainsInSelection.find((chain) => chain.id === approvalChainId) ?? null,
    [chainsInSelection, approvalChainId],
  );

  const affectedCount = selectedChain?.assetIds.length ?? 0;
  const canConfirm = !!selectedChain && affectedCount > 0 && !bulkUnassign.isPending;

  const handleClose = () => {
    if (bulkUnassign.isPending) return; // don't close mid-request
    onClose();
  };

  const handleConfirm = () => {
    if (!selectedChain) return;
    bulkUnassign.mutate(
      { approvalChainId: selectedChain.id, assetIds: selectedChain.assetIds },
      {
        onSuccess: (result) => {
          notificationService.showSuccess(
            result.skipped > 0
              ? t(
                  'assignAssetApprovalChain.bulkRemoveApprovalChainDialog.removedFromAssetsSkipped',
                  {
                    count: result.removed,
                    skipped: result.skipped,
                  },
                )
              : t('assignAssetApprovalChain.bulkRemoveApprovalChainDialog.removedFromAssets', {
                  count: result.removed,
                }),
          );
          onRemoved?.();
          onClose();
        },
        onError: (error) => {
          const { detail } = parseProblemDetail(
            error,
            t('assignAssetApprovalChain.bulkRemoveApprovalChainDialog.failedToRemoveApprovalChain'),
          );
          notificationService.showError(detail);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent
        className="max-w-120 gap-0 p-4"
        data-testid="bulk-remove-approval-chain-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {t('assignAssetApprovalChain.bulkRemoveApprovalChainDialog.removeApprovalChain')}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-4 pt-4">
          {chainsInSelection.length === 0 ? (
            <p className="text-foreground-alt py-4 text-center text-sm">
              {t(
                'assignAssetApprovalChain.bulkRemoveApprovalChainDialog.noneOfTheSelectedAssetsHave',
              )}
            </p>
          ) : (
            <>
              {chainsInSelection.length > 1 && (
                <div className="flex flex-col gap-1.5">
                  <span className="text-sm font-medium">
                    {t('assignAssetApprovalChain.bulkRemoveApprovalChainDialog.approvalChain')}
                  </span>
                  <Select
                    value={pickedChainId ?? undefined}
                    onValueChange={setPickedChainId}
                    disabled={bulkUnassign.isPending}
                  >
                    <SelectTrigger data-testid="bulk-remove-approval-chain-select">
                      <SelectValue
                        placeholder={t(
                          'assignAssetApprovalChain.bulkRemoveApprovalChainDialog.selectAnApprovalChain',
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {chainsInSelection.map((chain) => (
                        <SelectItem key={chain.id} value={chain.id}>
                          {t(
                            'assignAssetApprovalChain.bulkRemoveApprovalChainDialog.chainOnAssets',
                            { name: chain.name, count: chain.assetIds.length },
                          )}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {selectedChain && (
                <p className="text-foreground-alt text-sm">
                  {t(
                    'assignAssetApprovalChain.bulkRemoveApprovalChainDialog.thisRemovesNamedApprovalChainFrom',
                    { name: selectedChain.name, count: affectedCount },
                  )}
                </p>
              )}

              {selectedChain && affectedCount < selectedCount && (
                <p className="text-foreground-alt text-xs" data-testid="bulk-remove-skipped-note">
                  {t('assignAssetApprovalChain.bulkRemoveApprovalChainDialog.remainingAreSkipped', {
                    count: selectedCount - affectedCount,
                  })}
                </p>
              )}

              <p className="text-destructive text-xs" data-testid="bulk-remove-approvals-warning">
                {t(
                  'assignAssetApprovalChain.bulkRemoveApprovalChainDialog.approvedAssetsReturnToReview',
                )}
              </p>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={bulkUnassign.isPending}
          >
            {t('assignAssetApprovalChain.bulkRemoveApprovalChainDialog.cancel')}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm}
            data-testid="bulk-remove-confirm"
          >
            {bulkUnassign.isPending && <Spinner className="size-4" />}
            {t('assignAssetApprovalChain.bulkRemoveApprovalChainDialog.removeApprovalChain')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

BulkRemoveApprovalChainDialog.displayName = 'BulkRemoveApprovalChainDialog';
